/**
 * SequenceAPI - Firestore CRUD operations for sequences
 * Uses top-level 'sequences' collection for fast lookups and clean URLs
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { ExperienceConfig } from '../ui/UIManager';
import type { PortalEffectStyle } from '../types/config';
import * as bcrypt from 'bcryptjs';
import { Logger } from '../utils/Logger';
import { generateSlugFromName } from './SlugAPI';

const logger = Logger.getLogger('SequenceAPI');

export interface Sequence {
  id?: string;
  userId: string;
  orgId: string | null; // Organization ID (for Phase 2)
  name: string;
  description?: string;
  thumbnailUrl?: string; // Preview thumbnail (auto-generated or custom uploaded)
  isCustomThumbnail?: boolean; // True if user uploaded a custom thumbnail (disables auto-capture)
  config: ExperienceConfig;
  isPublic: boolean;
  isDiscoverable?: boolean; // Show on public discover page (requires isPublic: true)
  analyticsEnabled?: boolean; // Enable/disable analytics tracking for this sequence
  password: string | null; // Hashed password (for Phase 3)
  customDomain: string | null; // Custom domain (for Phase 4)
  embedEnabled: boolean;
  embedSettings?: {
    showControls?: boolean;
    showBranding?: boolean;
    autoplay?: boolean;
  };
  logoUrl?: string; // Custom logo URL for loading screen (Pro+ white-label feature)
  removeBranding?: boolean; // Hide XRGallery branding on loading screen (Pro+ white-label feature)
  removeFooterBranding?: boolean; // Hide "Powered by XRGallery" footer (Pro+ white-label feature)
  disableTransitions?: boolean; // Skip portal transition animations between stages
  disableGazeNavigation?: boolean; // Hide flashlight button and disable gaze/xray portal effect
  portalSettings?: {
    glowColor?: string;           // Leading edge glow color (hex), default '#00bfff'
    edgeOpacity?: number;         // Leading edge opacity (0-1), default 1.0
    effectStyle?: PortalEffectStyle; // Portal visual style: 'clean', 'enhanced', 'minimal', 'alien'
  };
  // Custom URL slugs
  globalSlug?: string | null;      // Pro-only: globally unique slug (e.g., xrgallery.online/my-cool-tour)
  userSlug?: string | null;        // All users: unique per user (e.g., xrgallery.online/username/tour-name)
  slugClaimedAt?: Timestamp | null; // When global slug was claimed (for grandfathering on downgrade)
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Create a new sequence (top-level collection)
 */
export async function createSequence(
  userId: string,
  name: string,
  config: ExperienceConfig,
  isPublic: boolean = false,
  description?: string,
  orgId?: string | null,
  password?: string | null,
  analyticsEnabled?: boolean,
  isDiscoverable?: boolean
): Promise<string> {
  try {
    const sequencesRef = collection(db, 'sequences');
    const newDocRef = doc(sequencesRef); // Auto-generate ID
    const now = Timestamp.now();

    // Hash password if provided
    let hashedPassword: string | null = null;
    if (password && password.trim() !== '') {
      hashedPassword = await bcrypt.hash(password, 10);
    }

    // Auto-generate userSlug from tour name
    let userSlug: string | null = null;
    const baseSlug = generateSlugFromName(name);
    if (baseSlug.length >= 3) {
      // Check if this slug is already used by this user
      const existingQuery = query(
        sequencesRef,
        where('userId', '==', userId),
        where('userSlug', '==', baseSlug)
      );
      const existingDocs = await getDocs(existingQuery);
      if (existingDocs.empty) {
        userSlug = baseSlug;
      } else {
        // Append a short suffix to make it unique
        const suffix = newDocRef.id.substring(0, 4).toLowerCase();
        const suffixedSlug = `${baseSlug}-${suffix}`.substring(0, 50);
        const suffixQuery = query(
          sequencesRef,
          where('userId', '==', userId),
          where('userSlug', '==', suffixedSlug)
        );
        const suffixDocs = await getDocs(suffixQuery);
        if (suffixDocs.empty) {
          userSlug = suffixedSlug;
        }
      }
    }

    const sequenceData: Omit<Sequence, 'id'> = {
      userId,
      orgId: orgId || null,
      name,
      description: description || '',
      config,
      isPublic,
      isDiscoverable: isPublic && (isDiscoverable === true), // Only discoverable if public and explicitly enabled
      analyticsEnabled: analyticsEnabled !== false, // Default to true if not specified
      password: hashedPassword,
      customDomain: null,
      disableGazeNavigation: true, // Flashlight mode off by default
      embedEnabled: true,
      embedSettings: {
        showControls: true,
        showBranding: true,
        autoplay: false
      },
      ...(userSlug && { userSlug }),
      createdAt: now,
      updatedAt: now
    };

    await setDoc(newDocRef, sequenceData);
    logger.info('Sequence created:', newDocRef.id, userSlug ? `(slug: ${userSlug})` : '(no slug)');
    return newDocRef.id;
  } catch (error) {
    logger.error('Error creating sequence:', error);
    throw new Error('Failed to create sequence');
  }
}

/**
 * Get a specific sequence by ID (direct lookup from top-level collection)
 */
export async function getSequence(sequenceId: string): Promise<Sequence | null> {
  try {
    const sequenceRef = doc(db, 'sequences', sequenceId);
    const sequenceSnap = await getDoc(sequenceRef);

    if (sequenceSnap.exists()) {
      return {
        id: sequenceSnap.id,
        ...sequenceSnap.data()
      } as Sequence;
    } else {
      logger.warn('Sequence not found:', sequenceId);
      return null;
    }
  } catch (error) {
    logger.error('Error getting sequence:', error);
    throw new Error('Failed to get sequence');
  }
}

/**
 * Update a sequence
 */
export async function updateSequence(
  sequenceId: string,
  updates: Partial<Omit<Sequence, 'id' | 'createdAt'>>
): Promise<void> {
  try {
    const sequenceRef = doc(db, 'sequences', sequenceId);

    // Always update the updatedAt timestamp
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    await updateDoc(sequenceRef, updateData);
    logger.info('Sequence updated:', sequenceId);
  } catch (error) {
    logger.error('Error updating sequence:', error);
    throw new Error('Failed to update sequence');
  }
}

/**
 * Get sequence by custom domain
 */
export async function getSequenceByDomain(domain: string): Promise<Sequence | null> {
  try {
    const sequencesRef = collection(db, 'sequences');
    const q = query(sequencesRef, where('customDomain', '==', domain));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as Sequence;
    }

    return null;
  } catch (error) {
    logger.error('Error getting sequence by domain:', error);
    return null;
  }
}

/**
 * Get all sequences for a user
 */
export async function listUserSequences(userId: string): Promise<Sequence[]> {
  try {
    const sequencesRef = collection(db, 'sequences');
    const q = query(
      sequencesRef,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const sequences: Sequence[] = [];
    querySnapshot.forEach((doc) => {
      sequences.push({
        id: doc.id,
        ...doc.data()
      } as Sequence);
    });

    logger.debug(`Found ${sequences.length} sequences for user ${userId}`);
    return sequences;
  } catch (error) {
    logger.error('Error listing sequences:', error);
    throw new Error('Failed to list sequences');
  }
}

/**
 * Get all sequences for an organization (Phase 2)
 */
export async function listOrgSequences(orgId: string): Promise<Sequence[]> {
  try {
    const sequencesRef = collection(db, 'sequences');
    const q = query(
      sequencesRef,
      where('orgId', '==', orgId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const sequences: Sequence[] = [];
    querySnapshot.forEach((doc) => {
      sequences.push({
        id: doc.id,
        ...doc.data()
      } as Sequence);
    });

    logger.debug(`Found ${sequences.length} sequences for organization ${orgId}`);
    return sequences;
  } catch (error) {
    logger.error('Error listing org sequences:', error);
    throw new Error('Failed to list organization sequences');
  }
}

/**
 * Get all discoverable public sequences for the discover page
 */
export async function listDiscoverableSequences(limitCount: number = 50): Promise<Sequence[]> {
  try {
    const sequencesRef = collection(db, 'sequences');
    const q = query(
      sequencesRef,
      where('isPublic', '==', true),
      where('isDiscoverable', '==', true),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const sequences: Sequence[] = [];
    let count = 0;
    querySnapshot.forEach((doc) => {
      if (count < limitCount) {
        sequences.push({
          id: doc.id,
          ...doc.data()
        } as Sequence);
        count++;
      }
    });

    logger.debug(`Found ${sequences.length} discoverable sequences`);
    return sequences;
  } catch (error) {
    logger.error('Error listing discoverable sequences:', error);
    throw new Error('Failed to list discoverable sequences');
  }
}

/**
 * Delete a sequence
 */
export async function deleteSequence(sequenceId: string): Promise<void> {
  try {
    const sequenceRef = doc(db, 'sequences', sequenceId);
    await deleteDoc(sequenceRef);
    logger.info('Sequence deleted:', sequenceId);
  } catch (error) {
    logger.error('Error deleting sequence:', error);
    throw new Error('Failed to delete sequence');
  }
}

/**
 * Toggle sequence visibility (public/private)
 */
export async function toggleSequenceVisibility(
  sequenceId: string,
  isPublic: boolean
): Promise<void> {
  try {
    await updateSequence(sequenceId, { isPublic });
    logger.info(`Sequence visibility set to ${isPublic ? 'public' : 'private'}`);
  } catch (error) {
    logger.error('Error toggling visibility:', error);
    throw new Error('Failed to toggle sequence visibility');
  }
}

/**
 * Duplicate a sequence
 */
export async function duplicateSequence(
  userId: string,
  sequenceId: string,
  newName?: string
): Promise<string> {
  try {
    // Get original sequence
    const original = await getSequence(sequenceId);
    if (!original) {
      throw new Error('Original sequence not found');
    }

    // Create duplicate with new name
    const duplicateName = newName || `${original.name} (Copy)`;
    const newSequenceId = await createSequence(
      userId,
      duplicateName,
      original.config,
      false, // Duplicates are private by default
      original.description
    );

    logger.info('Sequence duplicated:', newSequenceId);
    return newSequenceId;
  } catch (error) {
    logger.error('Error duplicating sequence:', error);
    throw new Error('Failed to duplicate sequence');
  }
}

/**
 * Get shareable link for a sequence (clean format)
 * Prefers slug-based URLs when available:
 * 1. Global slug: xrgallery.online/my-tour
 * 2. User slug: xrgallery.online/username/tour-name
 * 3. Fallback: xrgallery.online/{sequenceId}
 */
export function getShareableLink(sequenceId: string, sequence?: Sequence | null, username?: string | null): string {
  const baseUrl = window.location.origin;

  // Use username-prefixed slug if available
  if (username && sequence?.userSlug) {
    return `${baseUrl}/${username}/${sequence.userSlug}`;
  }

  // Fallback to sequence ID
  return `${baseUrl}/${sequenceId}`;
}

/**
 * Get embed code for a sequence
 * Uses slug-based URL when available
 */
export function getEmbedCode(sequenceId: string, options?: {
  width?: string;
  height?: string;
  controls?: boolean;
  branding?: boolean;
  autoplay?: boolean;
  sequence?: Sequence | null;
  username?: string | null;
}): string {
  const baseUrl = window.location.origin;

  // Determine the best URL path (username/slug or sequenceId)
  let urlPath = sequenceId;
  if (options?.username && options?.sequence?.userSlug) {
    urlPath = `${options.username}/${options.sequence.userSlug}`;
  }

  const params = new URLSearchParams({
    embed: 'true',
    ...(options?.controls === false && { controls: 'false' }),
    ...(options?.branding === false && { branding: 'false' }),
    ...(options?.autoplay === true && { autoplay: 'true' })
  });

  const width = options?.width || '100%';
  const height = options?.height || '600px';

  return `<iframe
  src="${baseUrl}/${urlPath}?${params}"
  width="${width}"
  height="${height}"
  frameborder="0"
  allowfullscreen
  allow="xr-spatial-tracking; gyroscope; accelerometer"
></iframe>`;
}

/**
 * Parse sequence path from URL
 * Supports: /{sequenceId}
 */
export function parseSequencePath(path: string): string | null {
  // Clean format: /{sequenceId}
  // Ignore: /, /dashboard, /dashboard/*
  const match = path.match(/^\/([^/]+)$/);

  if (match && match[1] && match[1] !== 'dashboard') {
    return match[1]; // Return sequenceId
  }

  return null;
}

/**
 * Verify password for a sequence
 */
export async function verifySequencePassword(
  sequenceId: string,
  password: string
): Promise<boolean> {
  try {
    const sequence = await getSequence(sequenceId);
    if (!sequence) {
      return false;
    }

    // If no password is set, allow access
    if (!sequence.password) {
      return true;
    }

    // Verify password
    return await bcrypt.compare(password, sequence.password);
  } catch (error) {
    logger.error('Error verifying password:', error);
    return false;
  }
}

/**
 * Check if a sequence requires a password
 */
export async function requiresPassword(sequenceId: string): Promise<boolean> {
  try {
    const sequence = await getSequence(sequenceId);
    return !!(sequence && sequence.password);
  } catch (error) {
    logger.error('Error checking password requirement:', error);
    return false;
  }
}

/**
 * Check if sequence is password protected
 */
export function isPasswordProtected(sequence: Sequence): boolean {
  return sequence.password !== null && sequence.password !== '';
}

/**
 * @deprecated Use verifySequencePassword instead - this is an alias for backwards compatibility
 */
export async function verifyPassword(sequenceId: string, password: string): Promise<boolean> {
  return verifySequencePassword(sequenceId, password);
}

/**
 * Check if current user can edit sequence
 */
export function canEditSequence(sequence: Sequence, currentUserId: string): boolean {
  // User is owner
  if (sequence.userId === currentUserId) {
    return true;
  }

  // TODO: Phase 2 - Check if user is member of org
  // if (sequence.orgId && userIsInOrg(currentUserId, sequence.orgId)) {
  //   return true;
  // }

  return false;
}
